import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"
import type { SeoMeta } from "@/types"
import SocialShareBar from "@/components/public/social-share-bar"

/* ------------------------------------------------------------------ */
/* FAQ                                                                 */
/* ------------------------------------------------------------------ */

const FAQ = [
  {
    q: "How many market appraisals should I do to win one instruction?",
    a: "The industry average conversion rate from market appraisal to instruction is around 30-40%. Top-performing agents convert 50-60% by investing in their presentation, marketing materials, and follow-up process. If you're below 30%, focus on improving your market appraisal pitch and marketing toolkit.",
  },
  {
    q: "What do vendors look for when choosing an agent?",
    a: "Research consistently shows the top factors are: local market knowledge, the agent's marketing quality (especially online presence and video), realistic pricing, communication style, and fee value. Interestingly, fee level ranks lower than marketing quality for most vendors.",
  },
  {
    q: "How soon should I follow up after a market appraisal?",
    a: "Send a follow-up email within 2 hours of the appraisal including your CMA, marketing plan, and a sample video. Then call the following day. Vendors often see 2-3 agents on the same day, so speed matters. The first agent to follow up with a compelling package has a significant advantage.",
  },
  {
    q: "Does video really help win instructions?",
    a: "Yes. 73% of vendors say they prefer the agent who offers video marketing. Showing a sample video at the market appraisal (ideally of a similar local property) gives the vendor a tangible preview of how their property will be marketed. It's one of the most effective differentiators available.",
  },
]

/* ------------------------------------------------------------------ */
/* Step card                                                           */
/* ------------------------------------------------------------------ */

function StepCard({
  number,
  title,
  children,
}: {
  number: string
  title: string
  children: React.ReactNode
}) {
  return (
    <div className="rounded-xl border border-stone bg-white p-6">
      <div className="flex items-start gap-4">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-red/10 font-display text-sm font-bold text-red">
          {number}
        </span>
        <div>
          <h3 className="font-display text-lg font-bold text-charcoal">
            {title}
          </h3>
          <div className="mt-2 space-y-2 text-sm leading-relaxed text-slate">
            {children}
          </div>
        </div>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export default function WinningInstructionsGuide({ meta }: { meta: SeoMeta }) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: "How to Win More Instructions as an Estate Agent",
            description: meta.description,
            publisher: {
              "@type": "Organization",
              name: "QuiteRight",
              url: "https://www.quiteright.co.uk",
              logo: {
                "@type": "ImageObject",
                url: "https://www.quiteright.co.uk/qr-logo-wordmark-red.png",
              },
            },
            datePublished: "2026-02-22",
            dateModified: "2026-02-22",
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          })}
        </script>
        <BreadcrumbSchema canonical={meta.canonical} items={[{ name: "Guides", path: "/guides" }, { name: "Winning Instructions" }]} />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
            Guide
          </p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl md:text-6xl">
            How to win more instructions
            <span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate">
            Proven strategies for converting market appraisals into vendor
            instructions. From preparation and comparable evidence to
            marketing presentation and follow-up.
          </p>
        </div>
      </section>

      <SocialShareBar />

      {/* Content */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            The instruction is everything<span className="text-red">.</span>
          </h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate">
            <p>
              Without instructions, nothing else matters. You can have the
              best negotiation skills in the business, but if vendors
              aren&rsquo;t choosing you over the competition, revenue
              doesn&rsquo;t grow. The good news: winning instructions is a
              skill that can be systematically improved.
            </p>
            <p>
              The agents who consistently win instructions do six things
              differently. Here&rsquo;s how to do each one well.
            </p>
          </div>

          {/* Steps */}
          <div className="mt-10 space-y-6">
            <StepCard number="1" title="Research before the appraisal">
              <p>
                Before you walk through the door, know the property inside
                out. Check the land registry, review the listing history,
                look at comparable sales and current competition. Pull up
                the EPC. Check planning applications in the area.
              </p>
              <p>
                Vendors are impressed by agents who arrive prepared. It
                signals professionalism and genuine interest in their
                property, not just another slot in the diary.
              </p>
            </StepCard>

            <StepCard number="2" title="Build rapport, then demonstrate expertise">
              <p>
                Spend the first 10-15 minutes listening. Ask about their
                timeline, motivations, and concerns. Understand what
                matters to them before talking about yourself.
              </p>
              <p>
                Then demonstrate local expertise. Share recent sale prices
                in their street, mention buyer trends, and show you
                understand the micro-market. Vendors choose agents who
                clearly know their patch.
              </p>
            </StepCard>

            <StepCard number="3" title="Present comparable evidence">
              <p>
                A well-prepared comparative market analysis (CMA) is your
                most powerful pricing tool. Include 3-5 recently sold
                comparables and 2-3 currently listed competitors. Show
                photos, sale prices, and time on market.
              </p>
              <p>
                Recommend a realistic price supported by the evidence.
                Vendors respect honesty; overvaluing to win the instruction
                leads to price reductions and vendor frustration down the
                line.
              </p>
            </StepCard>

            <StepCard number="4" title="Show your marketing in action">
              <p>
                This is where most agents fall short. Don&rsquo;t just
                describe your marketing; <strong>show it</strong>. Pull
                up a recent listing on your phone. Show the photography,
                the floorplan, the Rightmove listing.
              </p>
              <p>
                And play a video. If you can show a 30-second video ad of
                a similar local property, the vendor can see exactly how
                their home will be presented. 73% of vendors prefer the
                agent who offers video. This single step can be the
                difference between winning and losing the instruction.
              </p>
            </StepCard>

            <StepCard number="5" title="Justify your fee with confidence">
              <p>
                Don&rsquo;t apologise for your fee. Frame it in terms of
                value: the marketing investment, the likely sale price,
                and the net proceeds to the vendor. An agent who charges
                1.5% and achieves a sale price £15,000 higher than the
                competition has already paid for the fee difference many
                times over.
              </p>
              <p>
                If a vendor pushes on fee, highlight what&rsquo;s included:
                professional photography, video, social media campaigns,
                accompanied viewings, and proactive negotiation. Cheap
                agents who skip these steps cost vendors money in the long
                run.
              </p>
            </StepCard>

            <StepCard number="6" title="Follow up fast and follow up well">
              <p>
                Send a follow-up email within 2 hours. Include your CMA,
                a summary of your marketing plan, and a link to a sample
                video. Make it easy for the vendor to share with their
                partner or family.
              </p>
              <p>
                Call the next day. Be helpful, not pushy. Ask if they have
                questions, offer to revisit if needed, and confirm your
                availability. The agent who follows up first with the best
                material wins more often than not.
              </p>
            </StepCard>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-t border-stone bg-charcoal px-6 py-16">
        <ScrollReveal
          stagger
          className="mx-auto grid max-w-5xl grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate/20 bg-slate/20 sm:grid-cols-4"
        >
          {[
            { stat: "73%", label: "of vendors prefer agents offering video" },
            { stat: "40%", label: "say marketing influenced their agent choice" },
            { stat: "50%+", label: "conversion rate for top-performing agents" },
            { stat: "<60s", label: "to create a listing video with QuiteRight" },
          ].map((item) => (
            <div key={item.stat} className="bg-charcoal px-5 py-6 text-center">
              <p className="font-display text-2xl font-bold text-cream md:text-3xl">
                {item.stat}
              </p>
              <p className="mt-2 text-xs leading-snug text-warm-grey">{item.label}</p>
            </div>
          ))}
        </ScrollReveal>
      </section>

      {/* Related */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Related resources<span className="text-red">.</span>
          </h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {[
              { href: "/tools/instruction-calculator", title: "Instruction calculator", desc: "See how much one extra instruction is worth to your agency." },
              { href: "/guides/property-marketing-checklist", title: "Marketing checklist", desc: "The complete checklist for marketing every listing." },
            ].map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="group flex flex-col rounded-xl border border-stone bg-white p-5 transition-all hover:border-red/30 hover:shadow-sm"
              >
                <p className="font-display text-base font-bold text-charcoal transition-colors group-hover:text-red">
                  {item.title}
                </p>
                <p className="mt-1 flex-1 text-sm text-slate">{item.desc}</p>
                <p className="mt-3 flex items-center gap-1 text-xs font-medium text-red">
                  Explore
                  <svg viewBox="0 0 20 20" fill="currentColor" className="size-3.5 transition-transform group-hover:translate-x-0.5">
                    <path fillRule="evenodd" d="M5 10a.75.75 0 0 1 .75-.75h6.638L10.23 7.29a.75.75 0 1 1 1.04-1.08l3.5 3.25a.75.75 0 0 1 0 1.08l-3.5 3.25a.75.75 0 1 1-1.04-1.08l2.158-1.96H5.75A.75.75 0 0 1 5 10Z" clipRule="evenodd" />
                  </svg>
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal className="text-center">
            <h2 className="font-display text-3xl font-bold text-charcoal md:text-4xl">
              Common questions<span className="text-red">.</span>
            </h2>
          </ScrollReveal>
          <div className="mt-12 divide-y divide-stone">
            {FAQ.map((item) => (
              <details key={item.q} className="group py-5">
                <summary className="flex cursor-pointer items-center justify-between text-base font-medium text-charcoal">
                  {item.q}
                  <svg viewBox="0 0 20 20" fill="currentColor" className="size-5 shrink-0 text-warm-grey transition-transform group-open:rotate-180">
                    <path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
                  </svg>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-slate">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-stone bg-charcoal px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-cream md:text-4xl lg:text-5xl">
            Win the instruction before they call back
            <span className="text-red">.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-lg text-warm-grey">
            Show a professional video at your next market appraisal. QuiteRight
            creates listing videos in under 60 seconds, giving you the edge
            when it matters most.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href="/sign-up"
              className="inline-flex w-full items-center justify-center rounded-lg bg-red px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-deep-red sm:w-auto"
            >
              Start your free trial
            </Link>
            <Link
              href="/demo"
              className="inline-flex w-full items-center justify-center rounded-lg border border-cream/20 px-7 py-3.5 text-base font-medium text-cream transition-colors hover:border-cream/40 hover:bg-cream/5 sm:w-auto"
            >
              Book a demo
            </Link>
          </div>
          <p className="mt-5 text-sm text-warm-grey">
            14-day free trial &middot; No credit card &middot; Cancel any time
          </p>
        </div>
      </section>
    </PublicLayout>
  )
}
